// src/pages/ManageUsers.js
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";

const PRIMARY = "#1F2937";

function InviteModal({ onInvite, onClose }) {
  const [form,        setForm]        = useState({ email:"", name:"", jobTitle:"", department:"" });
  const [customDept,  setCustomDept]  = useState("");
  const [depts,       setDepts]       = useState([]);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(() => {
    fetch("/api/invite/departments").then(r=>r.json())
      .then(d => setDepts(d.departments || []))
      .catch(() => {});
  }, []);

  const isOther = form.department === "__other__";
  const effectiveDept = isOther ? customDept : form.department;

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const res = await fetch("/api/users/invite", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ ...form, department:effectiveDept, sendWelcome }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      onInvite(d.user);
    } catch(e){ setError(e.message); setSaving(false); }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{maxWidth:480}}>
        <div className="modal-header">
          <h2>Invite External Learner</h2>
          <button className="btn-ghost" onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:16,lineHeight:1.6}}>
            Invite an external (non-SUFC) learner. They can request a login link from the portal sign-in page.
          </p>
          <div className="form-group">
            <label>Full Name <span style={{color:"var(--red)"}}>*</span></label>
            <input value={form.name} onChange={e=>set("name",e.target.value)} required placeholder="Jane Smith"/>
          </div>
          <div className="form-group">
            <label>Email Address <span style={{color:"var(--red)"}}>*</span></label>
            <input type="email" value={form.email} onChange={e=>set("email",e.target.value)} required placeholder="jane@example.com"/>
          </div>
          <div className="form-row">
            <div className="form-group" style={{margin:0}}>
              <label>Job Title</label>
              <input value={form.jobTitle} onChange={e=>set("jobTitle",e.target.value)} placeholder="e.g. Account Manager"/>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label>Department</label>
              <select value={form.department} onChange={e=>set("department",e.target.value)}>
                <option value="">— Select —</option>
                {depts.map(d=><option key={d} value={d}>{d}</option>)}
                <option value="__other__">Other…</option>
              </select>
              {isOther && (
                <input style={{marginTop:6}} value={customDept} onChange={e=>setCustomDept(e.target.value)} placeholder="Enter department name"/>
              )}
            </div>
          </div>

          {/* Welcome email option */}
          <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",marginTop:8,
            padding:"12px 14px",background:sendWelcome?"#ECFDF5":"var(--gray-50)",
            borderRadius:"var(--radius)",border:`1.5px solid ${sendWelcome?"#10B981":"var(--gray-200)"}`,transition:"all 0.15s"}}>
            <input type="checkbox" checked={sendWelcome} onChange={e=>setSendWelcome(e.target.checked)}
              style={{width:"auto",accentColor:"#10B981",marginTop:2,flexShrink:0}}/>
            <div>
              <div style={{fontWeight:600,fontSize:13,color:sendWelcome?"#065F46":"var(--gray-700)"}}>
                Send welcome email with mandatory training details
              </div>
              <div style={{fontSize:12,color:"var(--gray-400)",marginTop:1}}>
                Notifies the learner they've been added to the portal and lists any mandatory courses they need to complete.
              </div>
            </div>
          </label>

          {error && <div style={{fontSize:13,color:"var(--red)",marginBottom:8,lineHeight:1.5,marginTop:10}}>{error}</div>}
          <div className="form-actions" style={{marginTop:16}}>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving||!form.email||!form.name}>
              {saving?"Adding…":"Add User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkInviteModal({ onDone, onClose }) {
  const [step,        setStep]        = useState("upload"); // upload | preview | result
  const [parsed,      setParsed]      = useState([]);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [result,      setResult]      = useState(null);
  const [parseError,  setParseError]  = useState(null);

  const REQUIRED = ["name","email"];
  const OPTIONAL = ["jobTitle","department"];
  const ALL_COLS  = [...REQUIRED,...OPTIONAL];

  function downloadTemplate() {
    const csv = [ALL_COLS.join(","), "Jane Smith,jane@example.com,Account Manager,Commercial"].join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = "sufc-learner-import-template.csv";
    a.click();
  }

  function parseCSV(text) {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) throw new Error("CSV must have a header row and at least one data row");
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/\s+/g,""));
    // Map CSV columns to our field names (flexible)
    const colMap = {
      name:       headers.findIndex(h => ["name","fullname","full name","full_name"].includes(h)),
      email:      headers.findIndex(h => ["email","emailaddress","email address","email_address"].includes(h)),
      jobTitle:   headers.findIndex(h => ["jobtitle","job title","job_title","title"].includes(h)),
      department: headers.findIndex(h => ["department","dept","team"].includes(h)),
    };
    if (colMap.name   < 0) throw new Error("CSV must have a 'name' column");
    if (colMap.email  < 0) throw new Error("CSV must have an 'email' column");

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map(c => c.trim().replace(/^"|"$/g,""));
      if (cols.every(c => !c)) continue; // skip blank rows
      const row = {
        name:       colMap.name >= 0       ? cols[colMap.name]||""       : "",
        email:      colMap.email >= 0      ? cols[colMap.email]||""      : "",
        jobTitle:   colMap.jobTitle >= 0   ? cols[colMap.jobTitle]||""   : "",
        department: colMap.department >= 0 ? cols[colMap.department]||"" : "",
        _row: i + 1,
      };
      const issues = [];
      if (!row.name.trim())  issues.push("Missing name");
      if (!row.email.trim()) issues.push("Missing email");
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) issues.push("Invalid email");
      row._issues = issues;
      rows.push(row);
    }
    if (!rows.length) throw new Error("No valid data rows found in CSV");
    return rows;
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setParseError(null);
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const rows = parseCSV(ev.target.result);
        setParsed(rows);
        setStep("preview");
      } catch(err) { setParseError(err.message); }
    };
    reader.readAsText(file);
  }

  async function handleImport() {
    setSaving(true);
    try {
      const valid = parsed.filter(r => !r._issues.length);
      const res = await fetch("/api/users/bulk-invite", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ users:valid.map(({name,email,jobTitle,department})=>({name,email,jobTitle,department})), sendWelcome }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setResult(d);
      setStep("result");
    } catch(e) { setParseError(e.message); }
    finally { setSaving(false); }
  }

  const valid   = parsed.filter(r => !r._issues.length);
  const invalid = parsed.filter(r => r._issues.length);

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{maxWidth:640}}>
        <div className="modal-header">
          <h2>Bulk Import Learners</h2>
          <button className="btn-ghost" onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button>
        </div>
        <div className="modal-body">

          {/* ── Step 1: Upload ───────────────────────────────────────────── */}
          {step === "upload" && (
            <>
              <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:16,lineHeight:1.6}}>
                Upload a CSV file to import multiple external learners at once. Download the template to ensure your file uses the correct format.
              </p>
              <div style={{background:"var(--gray-50)",border:"1px solid var(--gray-200)",borderRadius:8,padding:"16px 18px",marginBottom:16}}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Required columns</div>
                <div style={{fontSize:12,color:"var(--gray-500)",fontFamily:"monospace",background:"#fff",padding:"6px 10px",borderRadius:5,border:"1px solid var(--gray-200)"}}>
                  name, email
                </div>
                <div style={{fontSize:13,fontWeight:600,marginBottom:6,marginTop:10}}>Optional columns</div>
                <div style={{fontSize:12,color:"var(--gray-500)",fontFamily:"monospace",background:"#fff",padding:"6px 10px",borderRadius:5,border:"1px solid var(--gray-200)"}}>
                  jobTitle, department
                </div>
              </div>
              <div style={{display:"flex",gap:10,marginBottom:16}}>
                <button className="btn-secondary" onClick={downloadTemplate}>⬇ Download Template CSV</button>
              </div>
              {parseError && <div style={{fontSize:13,color:"var(--red)",marginBottom:10,padding:"8px 12px",background:"#FEF2F2",borderRadius:6}}>{parseError}</div>}
              <div className="form-group">
                <label>Upload CSV File</label>
                <input type="file" accept=".csv,text/csv" onChange={handleFile} style={{fontSize:13}}/>
              </div>
            </>
          )}

          {/* ── Step 2: Preview ─────────────────────────────────────────── */}
          {step === "preview" && (
            <>
              <div style={{display:"flex",gap:12,marginBottom:14}}>
                <div style={{background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:8,padding:"10px 16px",flex:1,textAlign:"center"}}>
                  <div style={{fontSize:22,fontWeight:800,color:"#065F46"}}>{valid.length}</div>
                  <div style={{fontSize:12,color:"#047857"}}>Ready to import</div>
                </div>
                {invalid.length > 0 && (
                  <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 16px",flex:1,textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:800,color:"#991B1B"}}>{invalid.length}</div>
                    <div style={{fontSize:12,color:"#DC2626"}}>Will be skipped (errors)</div>
                  </div>
                )}
              </div>

              {/* Preview table */}
              <div style={{maxHeight:280,overflowY:"auto",border:"1px solid var(--gray-200)",borderRadius:8,marginBottom:14}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{background:"var(--gray-50)",position:"sticky",top:0}}>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,borderBottom:"1px solid var(--gray-200)"}}>Name</th>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,borderBottom:"1px solid var(--gray-200)"}}>Email</th>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,borderBottom:"1px solid var(--gray-200)"}}>Job Title</th>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,borderBottom:"1px solid var(--gray-200)"}}>Dept</th>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,borderBottom:"1px solid var(--gray-200)"}}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((r,i) => (
                      <tr key={i} style={{background:r._issues.length?"#FEF2F2":"#fff",borderBottom:"1px solid var(--gray-100)"}}>
                        <td style={{padding:"7px 12px"}}>{r.name||<span style={{color:"var(--gray-400)"}}>—</span>}</td>
                        <td style={{padding:"7px 12px"}}>{r.email||<span style={{color:"var(--gray-400)"}}>—</span>}</td>
                        <td style={{padding:"7px 12px",color:"var(--gray-500)"}}>{r.jobTitle||"—"}</td>
                        <td style={{padding:"7px 12px",color:"var(--gray-500)"}}>{r.department||"—"}</td>
                        <td style={{padding:"7px 12px"}}>
                          {r._issues.length
                            ? <span style={{color:"#DC2626",fontSize:11}}>{r._issues.join(", ")}</span>
                            : <span style={{color:"#059669",fontSize:11}}>✓ OK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Welcome email option */}
              <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",marginBottom:16,
                padding:"12px 14px",background:sendWelcome?"#ECFDF5":"var(--gray-50)",
                borderRadius:"var(--radius)",border:`1.5px solid ${sendWelcome?"#10B981":"var(--gray-200)"}`,transition:"all 0.15s"}}>
                <input type="checkbox" checked={sendWelcome} onChange={e=>setSendWelcome(e.target.checked)}
                  style={{width:"auto",accentColor:"#10B981",marginTop:2,flexShrink:0}}/>
                <div>
                  <div style={{fontWeight:600,fontSize:13,color:sendWelcome?"#065F46":"var(--gray-700)"}}>
                    Send welcome email to all imported learners
                  </div>
                  <div style={{fontSize:12,color:"var(--gray-400)",marginTop:1}}>
                    Notifies each learner they've been added to the portal and lists any mandatory courses they need to complete.
                  </div>
                </div>
              </label>

              {parseError && <div style={{fontSize:13,color:"var(--red)",marginBottom:10,padding:"8px 12px",background:"#FEF2F2",borderRadius:6}}>{parseError}</div>}

              <div className="form-actions">
                <button className="btn-secondary" onClick={()=>{ setStep("upload"); setParsed([]); }}>← Back</button>
                <button className="btn-primary" onClick={handleImport} disabled={saving||!valid.length}>
                  {saving ? "Importing…" : `Import ${valid.length} Learner${valid.length!==1?"s":""}`}
                </button>
              </div>
            </>
          )}

          {/* ── Step 3: Result ──────────────────────────────────────────── */}
          {step === "result" && result && (
            <>
              <div style={{textAlign:"center",padding:"16px 0 24px"}}>
                <div style={{fontSize:48,marginBottom:12}}>✅</div>
                <div style={{fontSize:20,fontWeight:700,marginBottom:8}}>Import Complete</div>
                <div style={{display:"flex",justifyContent:"center",gap:16,flexWrap:"wrap",marginTop:16}}>
                  <div style={{background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:8,padding:"12px 20px",minWidth:100,textAlign:"center"}}>
                    <div style={{fontSize:26,fontWeight:800,color:"#065F46"}}>{result.summary.created}</div>
                    <div style={{fontSize:12,color:"#047857"}}>Created</div>
                  </div>
                  {result.summary.skipped > 0 && (
                    <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"12px 20px",minWidth:100,textAlign:"center"}}>
                      <div style={{fontSize:26,fontWeight:800,color:"#78350F"}}>{result.summary.skipped}</div>
                      <div style={{fontSize:12,color:"#92400E"}}>Skipped (already exist)</div>
                    </div>
                  )}
                  {result.summary.errors > 0 && (
                    <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,padding:"12px 20px",minWidth:100,textAlign:"center"}}>
                      <div style={{fontSize:26,fontWeight:800,color:"#991B1B"}}>{result.summary.errors}</div>
                      <div style={{fontSize:12,color:"#DC2626"}}>Errors</div>
                    </div>
                  )}
                </div>
                {sendWelcome && result.summary.created > 0 && (
                  <p style={{fontSize:13,color:"var(--gray-500)",marginTop:14}}>
                    Welcome emails sent to {result.summary.created} learner{result.summary.created!==1?"s":""}.
                  </p>
                )}
              </div>
              <div className="form-actions" style={{justifyContent:"center"}}>
                <button className="btn-primary" onClick={onDone}>Done</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkAssignModal({ emails, courses, onDone, onClose }) {
  const [courseId,   setCourseId]   = useState("");
  const [action,     setAction]     = useState("assign");
  const [mandatory,  setMandatory]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [emailStep,  setEmailStep]  = useState(null); // { recipientCount, recipients, templateKey }

  const selectedCourse = courses.find(c => c.id === courseId);

  async function handleSave() {
    if (!courseId) return;
    if (action === "unassign") { await doAssign(false); return; }
    // For assign: fetch preview count first
    setSaving(true); setError(null);
    try {
      const d = await fetch(`/api/courses/${courseId}/notify-preview`).then(r=>r.json());
      const relevantRecipients = (d.recipients||[]).filter(r => emails.includes(r.email));
      const templateKey = mandatory ? "course-assigned-mandatory" : "course-assigned-optional";
      if (relevantRecipients.length > 0) {
        setSaving(false);
        setEmailStep({ recipientCount: relevantRecipients.length, recipients: relevantRecipients, templateKey });
      } else {
        await doAssign(false);
      }
    } catch { await doAssign(false); }
    finally { setSaving(false); }
  }

  async function doAssign(sendEmails) {
    setSaving(true); setError(null);
    try {
      const res = await fetch("/api/users/assign", { method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ courseId, emails, action, mandatory }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      if (sendEmails && emailStep?.recipients?.length) {
        fetch(`/api/courses/${courseId}/notify`, { method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ recipients: emailStep.recipients, templateKey: emailStep.templateKey }) }).catch(()=>{});
      }
      onDone();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  if (emailStep) {
    return (
      <div className="modal-backdrop">
        <div className="modal" style={{maxWidth:480}}>
          <div className="modal-header">
            <h2>📧 Send Notification?</h2>
          </div>
          <div className="modal-body">
            <p style={{fontSize:14,lineHeight:1.7,color:"var(--gray-700)",marginBottom:12}}>
              You are assigning <strong>{emails.length} user{emails.length!==1?"s":""}</strong> to <strong>{selectedCourse?.name}</strong>
              {mandatory ? " as mandatory training" : " as optional training"}.
            </p>
            <div style={{background:"#EFF6FF",border:"1px solid #93C5FD",borderRadius:8,padding:"12px 16px",marginBottom:20}}>
              <div style={{fontWeight:600,fontSize:14,color:"#1D4ED8",marginBottom:4}}>
                📧 {emailStep.recipientCount} email{emailStep.recipientCount!==1?"s":""} would be sent
              </div>
              <div style={{fontSize:13,color:"#1E40AF"}}>
                Notifying {emailStep.recipientCount===1?"this user":"these users"} that {mandatory?"mandatory training has been assigned requiring completion":"optional training is now available"}.
              </div>
            </div>
            {error && <div style={{fontSize:13,color:"var(--red)",marginBottom:12}}>{error}</div>}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <button className="btn-primary" style={{background:PRIMARY}} onClick={()=>doAssign(true)} disabled={saving}>
                ✉ Assign and send {emailStep.recipientCount} email{emailStep.recipientCount!==1?"s":""}
              </button>
              <button className="btn-secondary" onClick={()=>doAssign(false)} disabled={saving}>
                Assign without sending emails
              </button>
              <button className="btn-ghost" style={{color:"var(--gray-500)"}} onClick={()=>{ setEmailStep(null); }} disabled={saving}>
                Do not assign
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{maxWidth:480}}>
        <div className="modal-header"><h2>{action==="assign"?"Assign":"Unassign"} Course — {emails.length} user{emails.length!==1?"s":""}</h2><button className="btn-ghost" onClick={onClose}><svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg></button></div>
        <div className="modal-body">
          <div className="form-group"><label>Course</label>
            <select value={courseId} onChange={e=>setCourseId(e.target.value)}>
              <option value="">— Select course —</option>
              {courses.filter(c=>c.status==="live").map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Action</label>
            <div style={{display:"flex",gap:12,marginTop:4}}>
              {[["assign","Assign to course"],["unassign","Unassign from course"]].map(([v,l])=>(
                <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontWeight:400}}>
                  <input type="radio" name="ba_action" value={v} checked={action===v} onChange={()=>setAction(v)} style={{accentColor:PRIMARY,width:"auto"}}/>{l}
                </label>
              ))}
            </div>
          </div>
          {action==="assign"&&(
            <div className="form-group"><label>Training Type</label>
              <div style={{display:"flex",gap:12,marginTop:4}}>
                {[["optional","Optional"],["mandatory","Mandatory"]].map(([v,l])=>(
                  <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontWeight:400}}>
                    <input type="radio" name="ba_mandatory" value={v} checked={mandatory===(v==="mandatory")} onChange={()=>setMandatory(v==="mandatory")} style={{accentColor:PRIMARY,width:"auto"}}/>{l}
                  </label>
                ))}
              </div>
              {courseId && action==="assign" && (
                <p style={{fontSize:12,color:"var(--gray-400)",marginTop:6}}>
                  You will be prompted about sending notification emails on the next step.
                </p>
              )}
            </div>
          )}
          {error&&<div style={{fontSize:13,color:"var(--red)",marginBottom:8}}>{error}</div>}
          <div className="form-actions">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving||!courseId}>{saving?"Checking…":action==="assign"?"Next →":"Unassign"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssignInline({ courseId, email, assigned, mandatory, onDone }) {
  const [picking,   setPicking]   = useState(false); // show mandatory picker
  const [saving,    setSaving]    = useState(false);

  async function doAssign(isMandatory) {
    setSaving(true);
    await fetch("/api/users/assign", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ courseId, emails:[email], action:"assign", mandatory:isMandatory }),
    });
    setSaving(false); setPicking(false); onDone();
  }

  async function doUnassign() {
    setSaving(true);
    await fetch("/api/users/assign", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ courseId, emails:[email], action:"unassign" }),
    });
    setSaving(false); onDone();
  }

  if (saving) return <span style={{fontSize:12,color:"var(--gray-400)"}}>Saving…</span>;

  if (assigned) {
    return (
      <div style={{display:"flex",gap:4,alignItems:"center"}}>
        <button className="btn-ghost" style={{fontSize:11,padding:"2px 6px"}}
          onClick={()=>doAssign(!mandatory)}
          title={`Change to ${mandatory?"Optional":"Mandatory"}`}>
          {mandatory?"→ Optional":"→ Mandatory"}
        </button>
        <button className="btn-ghost" style={{fontSize:11,padding:"2px 6px",color:"var(--red)"}}
          onClick={doUnassign}>
          Unassign
        </button>
      </div>
    );
  }

  if (picking) {
    return (
      <div style={{display:"flex",gap:4,alignItems:"center"}}>
        <span style={{fontSize:11,color:"var(--gray-500)"}}>Assign as:</span>
        <button className="btn-ghost" style={{fontSize:11,padding:"2px 8px",background:"#F0F4F8",border:"1px solid var(--gray-200)"}}
          onClick={()=>doAssign(false)}>Optional</button>
        <button className="btn-ghost" style={{fontSize:11,padding:"2px 8px",background:"#FEF3C7",border:"1px solid #F59E0B",color:"#92400E"}}
          onClick={()=>doAssign(true)}>Mandatory</button>
        <button className="btn-ghost" style={{fontSize:11,padding:"2px 4px",color:"var(--gray-400)"}}
          onClick={()=>setPicking(false)}>✕</button>
      </div>
    );
  }

  return (
    <button className="btn-ghost" style={{fontSize:12,padding:"3px 8px",color:PRIMARY}}
      onClick={()=>setPicking(true)}>
      Assign
    </button>
  );
}

function UserCoursesRow({ user }) {
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [policies,     setPolicies]     = useState(null);
  const [policyAcc,    setPolicyAcc]    = useState([]);
  const [policyLoad,   setPolicyLoad]   = useState(true);

  const loadCourses = useCallback(async()=>{
    setLoading(true);
    const d=await fetch(`/api/users/${encodeURIComponent(user.email)}/courses`).then(r=>r.json());
    setData(d.courses||[]); setLoading(false);
  },[user.email]);

  useEffect(()=>{
    loadCourses();
    // Load policies and this user's acceptances
    Promise.all([
      fetch("/api/policies").then(r=>r.json()),
      fetch(`/api/policies-my`).then(r=>r.json()), // reuse my endpoint — but we need admin view per user
    ]).then(([p]) => {
      const allPolicies = (p.policies||[]).filter(pol => pol.requireAcceptance);
      setPolicies(allPolicies);
      // Fetch acceptances for each policy to find this user's status
      return Promise.all(allPolicies.map(pol =>
        fetch(`/api/policies/${pol.id}/acceptance`).then(r=>r.json())
          .then(d => ({ policyId:pol.id, acceptances: d.acceptances||[] }))
          .catch(()=>({ policyId:pol.id, acceptances:[] }))
      ));
    }).then(results => {
      const accMap = {};
      results.forEach(({policyId, acceptances}) => {
        const userAcc = acceptances.find(a => a.email === user.email);
        accMap[policyId] = userAcc || null;
      });
      setPolicyAcc(accMap);
    }).catch(()=>{}).finally(()=>setPolicyLoad(false));
  },[user.email, loadCourses]);

  return (
    <tr>
      <td colSpan={7} style={{padding:0,background:"var(--gray-50)",borderBottom:"1px solid var(--gray-200)"}}>
        <div style={{padding:"14px 20px 18px 56px"}}>
          {/* Course Assignments */}
          <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",color:"var(--gray-400)",marginBottom:10}}>Course Assignments</div>
          {loading&&<div style={{padding:"8px 0"}}><span className="spinner" style={{width:18,height:18}}/></div>}
          {!loading&&(!data||data.length===0)&&<p style={{fontSize:13,color:"var(--gray-400)"}}>No courses in your department.</p>}
          {!loading&&data&&data.length>0&&(()=>{
            const visible=data.filter(({course,progress})=>
              course.status==="live" ||
              (course.status==="retired"&&progress.status==="completed")
            );
            if(!visible.length) return <p style={{fontSize:13,color:"var(--gray-400)"}}>No live courses in your department.</p>;
            return (
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead><tr style={{borderBottom:"1px solid var(--gray-200)"}}>{["Course","Course Status","Completion","Score","Completed","Type","Assignment",""].map((h,i)=><th key={i} style={{textAlign:"left",padding:"4px 12px 6px 0",color:"var(--gray-400)",fontWeight:600,fontSize:11}}>{h}</th>)}</tr></thead>
              <tbody>
                {visible.map(({course,progress,assigned,mandatory})=>(
                  <tr key={course.id} style={{borderBottom:"1px solid var(--gray-100)"}}>
                    <td style={{padding:"8px 12px 8px 0",fontWeight:500}}>{course.name}</td>
                    <td style={{padding:"8px 12px 8px 0"}}>
                      {course.status==="live"   &&<span className="badge badge-ok"  style={{fontSize:10}}>Live</span>}
                      {course.status==="retired"&&<span className="badge badge-due" style={{fontSize:10}}>Retired</span>}
                    </td>
                    <td style={{padding:"8px 12px 8px 0"}}>
                      {progress.status==="completed"  &&<span className="badge badge-ok"      style={{fontSize:11}}>Completed</span>}
                      {progress.status==="in_progress"&&<span className="badge badge-due"     style={{fontSize:11}}>In Progress</span>}
                      {(!progress.status||progress.status==="not_started")&&<span className="badge badge-neutral" style={{fontSize:11}}>Not Started</span>}
                    </td>
                    <td style={{padding:"8px 12px 8px 0",color:"var(--gray-600)"}}>{progress.score!=null?`${progress.score}%`:"—"}</td>
                    <td style={{padding:"8px 12px 8px 0",color:"var(--gray-600)"}}>{progress.completedAt?new Date(progress.completedAt).toLocaleDateString("en-GB"):"—"}</td>
                    <td style={{padding:"8px 12px 8px 0"}}>
                      {course.availabilityMode==="assigned_only"&&assigned
                        ?<span className={`badge ${mandatory?"badge-overdue":"badge-neutral"}`} style={{fontSize:11}}>{mandatory?"Mandatory":"Optional"}</span>
                        :course.availabilityMode==="available_to_all"
                          ?<span className={`badge ${course.mandatoryMode==="mandatory"?"badge-overdue":"badge-neutral"}`} style={{fontSize:11}}>{course.mandatoryMode==="mandatory"?"Mandatory":"Optional"}</span>
                          :<span style={{fontSize:12,color:"var(--gray-400)"}}>—</span>
                      }
                    </td>
                    <td style={{padding:"8px 12px 8px 0"}}>
                      {course.availabilityMode==="assigned_only"
                        ?<span className={`badge ${assigned?"badge-ok":"badge-neutral"}`} style={{fontSize:11}}>{assigned?"Assigned":"Not assigned"}</span>
                        :<span style={{fontSize:12,color:"var(--gray-400)"}}>Available to all</span>
                      }
                    </td>
                    <td style={{padding:"8px 0",textAlign:"right"}}>
                      {course.availabilityMode==="assigned_only"&&(
                        <AssignInline courseId={course.id} email={user.email} assigned={assigned} mandatory={mandatory} onDone={loadCourses}/>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            );
          })()}

          {/* Policy Acceptance */}
          {!policyLoad && policies && policies.length > 0 && (
            <div style={{marginTop:18}}>
              <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",color:"var(--gray-400)",marginBottom:10}}>Policy Acceptance</div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:"1px solid var(--gray-200)"}}>
                    {["Policy","Version","Status","Accepted On"].map((h,i)=>(
                      <th key={i} style={{textAlign:"left",padding:"4px 12px 6px 0",color:"var(--gray-400)",fontWeight:600,fontSize:11}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {policies.map(pol => {
                    const acc = policyAcc[pol.id];
                    const current = acc && acc.version === pol.version;
                    return (
                      <tr key={pol.id} style={{borderBottom:"1px solid var(--gray-100)"}}>
                        <td style={{padding:"8px 12px 8px 0",fontWeight:500}}>{pol.name}</td>
                        <td style={{padding:"8px 12px 8px 0",color:"var(--gray-500)"}}>v{pol.version}</td>
                        <td style={{padding:"8px 12px 8px 0"}}>
                          {current
                            ? <span className="badge badge-ok" style={{fontSize:11}}>✓ Accepted</span>
                            : acc
                              ? <span className="badge badge-due" style={{fontSize:11}}>⚠ Old version</span>
                              : <span className="badge badge-neutral" style={{fontSize:11}}>Pending</span>}
                        </td>
                        <td style={{padding:"8px 12px 8px 0",color:"var(--gray-500)"}}>
                          {acc?.acceptedAt ? new Date(acc.acceptedAt).toLocaleDateString("en-GB") : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!policyLoad && policies && policies.length === 0 && (
            <div style={{marginTop:14,fontSize:13,color:"var(--gray-400)"}}>No policies requiring acceptance.</div>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function ManageUsers() {
  const { user }     = useAuth();
  const [users,      setUsers]     = useState([]);
  const [courses,    setCourses]   = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [filter,     setFilter]    = useState("all");
  const [search,     setSearch]    = useState("");
  const [showInvite, setInvite]    = useState(false);
  const [selected,   setSelected]  = useState(new Set());
  const [expanded,   setExpanded]  = useState(null);
  const [bulkModal,  setBulkModal]  = useState(false);
  const [bulkInvite, setBulkInvite] = useState(false);
  const [resending,  setResending]  = useState(null);

  const load = useCallback(async()=>{
    setLoading(true);
    const [u,c]=await Promise.all([fetch("/api/users").then(r=>r.json()),fetch("/api/courses").then(r=>r.json())]);
    setUsers(u.users||[]); setCourses(c.courses||[]); setLoading(false);
  },[]);
  useEffect(()=>{load();},[load]);

  async function handleArchive(u){
    if(!window.confirm(`Archive ${u.name||u.email}? They will no longer be able to log in but their training history is preserved.`)) return;
    const res=await fetch(`/api/users/${encodeURIComponent(u.email)}/archive`,{method:"POST"});
    if(res.ok) setUsers(prev=>prev.map(x=>x.email===u.email?{...x,active:false,statusLabel:"Archived",archivedAt:new Date().toISOString()}:x));
    else { const d=await res.json(); alert(d.error); }
  }
  async function handleActivate(u){
    const res=await fetch(`/api/users/${encodeURIComponent(u.email)}/activate`,{method:"POST"});
    if(res.ok) setUsers(prev=>prev.map(x=>x.email===u.email?{...x,active:true,statusLabel:"Active",archivedAt:null}:x));
    else { const d=await res.json(); alert(d.error); }
  }
  async function handleDelete(u){
    if(!window.confirm(`Move ${u.name||u.email} to the recycle bin? Admins can permanently delete or restore from there.`)) return;
    const res=await fetch(`/api/users/${encodeURIComponent(u.email)}`,{method:"DELETE"});
    if(res.ok) setUsers(prev=>prev.filter(x=>x.email!==u.email));
    else { const d=await res.json(); alert(d.error); }
  }
  async function handleResend(u){
    setResending(u.email);
    try{
      const res=await fetch(`/api/users/${encodeURIComponent(u.email)}/resend-invite`,{method:"POST"});
      const data=await res.json(); if(!res.ok) throw new Error(data.error);
      alert(`Login link (7 days):\n\n${data.link}`);
    }catch(e){alert(e.message);}finally{setResending(null);}
  }

  const filtered=users.filter(u=>{
    const isArchived = u.statusLabel==="Archived";
    if(filter==="all"     && isArchived) return false; // archived hidden from All
    if(filter==="m365"    && (u.isExternal||isArchived)) return false;
    if(filter==="external"&& (!u.isExternal||isArchived)) return false;
    if(filter==="archived"&& !isArchived) return false;
    if(search&&!`${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const allChecked =filtered.length>0&&filtered.every(u=>selected.has(u.email));
  const someChecked=filtered.some(u=>selected.has(u.email));
  function toggleSelectAll(){
    if(allChecked) setSelected(new Set());
    else setSelected(new Set(filtered.map(u=>u.email)));
  }
  function toggleSelect(email){
    setSelected(prev=>{const next=new Set(prev);next.has(email)?next.delete(email):next.add(email);return next;});
  }

  const assignableCourses=user?.role==="admin"?courses:courses.filter(c=>c.categoryId===user?.categoryId);

  const FILTERS=[["all","All"],["m365","Microsoft 365"],["external","External"],["active","Active"],["archived","Archived"]];

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Manage Users</div><div className="page-subtitle">Microsoft 365 staff and invited external trainees</div></div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {selected.size>0&&<button className="btn-secondary" onClick={()=>setBulkModal(true)}>Assign Course ({selected.size} selected)</button>}
          <button className="btn-secondary" onClick={()=>setBulkInvite(true)}>⬆ Bulk Import</button>
          <button className="btn-primary" style={{background:PRIMARY}} onClick={()=>setInvite(true)}>+ Invite External Learner</button>
        </div>
      </div>

      <div className="card" style={{padding:0,overflow:"hidden"}}>
        <div className="policies-toolbar">
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {FILTERS.map(([val,label])=>(
              <button key={val} className={`btn-secondary ${filter===val?"active":""}`} style={{fontSize:12,padding:"4px 10px"}}
                onClick={()=>{setFilter(val);setSelected(new Set());}}>
                {label}
              </button>
            ))}
          </div>
          <input placeholder="Search name or email…" value={search} onChange={e=>setSearch(e.target.value)} style={{width:220}}/>
        </div>

        {loading
          ?<div className="page-loading"><span className="spinner" style={{width:24,height:24}}/></div>
          :filtered.length===0
            ?<div className="empty-state"><h3>No users found</h3><p>Try adjusting the filter or invite a user.</p></div>
            :(
              <table className="contacts-table">
                <thead>
                  <tr>
                    <th style={{width:36,paddingLeft:16,flexShrink:0}}>
                      <input type="checkbox" checked={allChecked} ref={el=>{if(el)el.indeterminate=someChecked&&!allChecked;}} onChange={toggleSelectAll} style={{accentColor:PRIMARY,width:14,height:14}}/>
                    </th>
                    <th>Name</th><th>Email</th><th>Job Title</th><th>Department</th><th>Type</th><th>Last Login</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u=>(
                    <React.Fragment key={u.email}>
                      <tr className="contact-row" style={{background:selected.has(u.email)?"#F0F4F8":"",cursor:"pointer"}}>
                        <td style={{paddingLeft:16}} onClick={e=>e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(u.email)} onChange={()=>toggleSelect(u.email)} style={{accentColor:PRIMARY,width:14,height:14}}/>
                        </td>
                        <td className="contact-cell" onClick={()=>setExpanded(expanded===u.email?null:u.email)}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div className="user-avatar" style={{width:28,height:28,fontSize:11,background:PRIMARY,flexShrink:0}}>
                              {(u.name||u.email).split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                            </div>
                            <div>
                              <div style={{fontWeight:600,fontSize:13}}>{u.name||"—"}</div>
                              {u.notYetLogged&&<div style={{fontSize:11,color:"var(--gray-400)"}}>Not yet signed in</div>}
                            </div>
                          </div>
                        </td>
                        <td className="contact-cell" style={{fontSize:12,color:"var(--gray-500)"}} onClick={()=>setExpanded(expanded===u.email?null:u.email)}>{u.email}</td>
                        <td className="contact-cell" style={{fontSize:12,color:"var(--gray-600)"}} onClick={()=>setExpanded(expanded===u.email?null:u.email)}>{u.jobTitle||"—"}</td>
                        <td className="contact-cell" style={{fontSize:12,color:"var(--gray-600)"}} onClick={()=>setExpanded(expanded===u.email?null:u.email)}>{u.m365Department||"—"}</td>
                        <td className="contact-cell" onClick={()=>setExpanded(expanded===u.email?null:u.email)}>
                          <span className={`badge ${u.isExternal?"badge-due":"badge-ok"}`} style={{fontSize:11}}>{u.isExternal?"External":"M365"}</span>
                        </td>
                        <td className="contact-cell" style={{fontSize:12,color:"var(--gray-400)"}} onClick={()=>setExpanded(expanded===u.email?null:u.email)}>
                          {u.lastLogin?new Date(u.lastLogin).toLocaleDateString("en-GB"):"Never"}
                        </td>
                        <td className="contact-cell" onClick={()=>setExpanded(expanded===u.email?null:u.email)}>
                          <span className={`badge ${u.statusLabel==="Active"?"badge-ok":"badge-neutral"}`} style={{fontSize:11}}>{u.statusLabel||"Active"}</span>
                        </td>
                        <td className="contact-cell">
                          <div style={{display:"flex",gap:4,justifyContent:"flex-end",alignItems:"center"}}>
                            {u.isExternal&&<button className="btn-ghost" style={{fontSize:12,padding:"3px 8px"}} onClick={()=>handleResend(u)} disabled={resending===u.email}>{resending===u.email?"…":"Get Link"}</button>}
                            {/* Archive — external active users only */}
                            {u.isExternal&&u.statusLabel==="Active"&&<button className="btn-ghost" style={{fontSize:12,padding:"3px 8px",color:"var(--gray-500)"}} onClick={()=>handleArchive(u)}>Archive</button>}
                            {/* Activate — archived external users */}
                            {u.isExternal&&u.statusLabel==="Archived"&&<button className="btn-ghost" style={{fontSize:12,padding:"3px 8px",color:"#1F2937"}} onClick={()=>handleActivate(u)}>Activate</button>}
                            {/* Delete to bin — archived users only, admin only */}
                            {user?.role==="admin"&&u.statusLabel==="Archived"&&<button className="btn-ghost" style={{fontSize:12,padding:"3px 8px",color:"var(--red)"}} onClick={()=>handleDelete(u)}>Delete</button>}
                            <span style={{fontSize:16,color:"var(--gray-400)",padding:"0 4px"}}>{expanded===u.email?"▾":"▸"}</span>
                          </div>
                        </td>
                      </tr>
                      {expanded===u.email&&<UserCoursesRow user={u}/>}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )
        }
      </div>

      {showInvite&&<InviteModal onInvite={u=>{setUsers(p=>[...p,u]);setInvite(false);}} onClose={()=>setInvite(false)}/>}
      {bulkInvite&&<BulkInviteModal onDone={()=>{setBulkInvite(false);load();}} onClose={()=>setBulkInvite(false)}/>}
      {bulkModal&&<BulkAssignModal emails={[...selected]} courses={assignableCourses} onDone={()=>{setBulkModal(false);setSelected(new Set());setExpanded(null);}} onClose={()=>setBulkModal(false)}/>}
    </div>
  );
}
